import { Paging, TableSortDirection} from "@triumph/design-system"
import { DataTable } from "@/app/shared/components/DataTable/DataTable"
import { TableTitleHeader } from "@/app/shared/components/DataTable/Components/TableTitleHeader"
import { AvailableFilters, InvoicesDashboard } from '../dashboard.types';
import { invoicesTableColumns } from './invoicesTableColumns';
import { ButtonFilterSelection } from '@/app/shared/utils/invoiceUtils/invoiceTypes';
import { InvoicesTableFilters } from './InvoicesTableFilters';
import { DashboardTableWrapper } from './Invoices.StyledComponents';
import { PaddedCard } from '../Dashboard.StyledComponents';
import { InvoicesTableHeader } from './InvoicesTableHeader';
import { FileType } from '@/app/shared/constants/misc/FileType';

interface InvoicesTableProps {
  rows: InvoicesDashboard[];
  totalInvoices: number;
  availableFilters: AvailableFilters;
  currentPage: number;
  pageSize: number;
  isLoading?: boolean;
  handleNotesChange: (
    invoiceNumber: string,
    includeDisputedReasons: boolean,
  ) => void;
  handlePaymentDateChange: (
    invoiceNumber: string,
    selectedPaymentDate: string,
  ) => void;
  handleOnSort: (field: string, direction?: TableSortDirection) => void;
  handleDateRangeChange: (
    index: number,
    startDate: Date,
    endDate: Date,
  ) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  needsAttention: boolean;
  handleNeedsAttentionChange: (needsAttention: boolean) => void;
  selectedDateRangeIndex: number;
  currentDateRange: { startDate: Date; endDate: Date } | null;
  selectedInvoices: Set<string>;
  onSelectInvoice: (invoiceNumber: string, isSelected: boolean) => void;
  onSelectAllOnPage: (isSelected: boolean) => void;
  allCurrentPageSelected: boolean;
  someCurrentPageSelected: boolean;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  buttonFilters: ButtonFilterSelection[];
  onButtonFilterChange: (column: string, value: string) => Promise<void>;
  onExportList: (fileType: FileType) => void;
  isExporting?: boolean;
  onUpdatePaymentStatus?: () => void;
  onBulkInvoiceDetails?: () => void;
  getDisplayValueById?: (id: string | number | null | undefined) => string;
  onResetFilters: () => void;
  hasActiveFilters: boolean;
  filterResetKey: number;
}

export const InvoicesTable = ({
  rows,
  totalInvoices,
  availableFilters,
  currentPage,
  pageSize,
  isLoading,
  handleNotesChange,
  handlePaymentDateChange,
  handleOnSort,
  handleDateRangeChange,
  handlePageChange,
  handlePageSizeChange,
  selectedDateRangeIndex,
  currentDateRange,
  selectedInvoices,
  onSelectInvoice,
  onSelectAllOnPage,
  needsAttention,
  handleNeedsAttentionChange,
  allCurrentPageSelected,
  someCurrentPageSelected,
  searchTerm,
  onSearchChange,
  buttonFilters,
  onButtonFilterChange,
  onExportList,
  isExporting = false,
  onUpdatePaymentStatus,
  onBulkInvoiceDetails,
  getDisplayValueById,
  onResetFilters,
  hasActiveFilters,
  filterResetKey,
}: InvoicesTableProps) => {
  const columnFormat = invoicesTableColumns({
    handlePaymentDateChange,
    handleNotesChange,
    selectedInvoices,
    onSelectInvoice,
    onSelectAllOnPage,
    allCurrentPageSelected,
    someCurrentPageSelected,
    getDisplayValueById,
  });

  const onTableSort = (field: string, direction: TableSortDirection) => {
    handleOnSort(field, direction);
  };

  return (
    <PaddedCard isLarge={true}>
      <InvoicesTableHeader />
      <InvoicesTableFilters
        rows={rows}
        totalInvoices={totalInvoices}
        availableFilters={availableFilters}
        selectedDateRangeIndex={selectedDateRangeIndex}
        currentDateRange={currentDateRange}
        handleDateRangeChange={handleDateRangeChange}
        needsAttention={needsAttention}
        handleNeedsAttentionChange={handleNeedsAttentionChange}
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        buttonFilters={buttonFilters}
        onButtonFilterChange={onButtonFilterChange}
        onExportList={onExportList}
        isExporting={isExporting}
        onResetFilters={onResetFilters}
        hasActiveFilters={hasActiveFilters}
        filterResetKey={filterResetKey}
      />
      <DashboardTableWrapper>
        <TableTitleHeader
          title={'Invoices'}
          actionButtons={true}
          onUpdatePaymentStatus={onUpdatePaymentStatus}
          onBulkInvoiceDetails={onBulkInvoiceDetails}
        />
        <DataTable
          data={rows ?? []}
          columns={columnFormat}
          isShowOverflow={true}
          textWrapMode={2}
          alternateRowColor={true}
          maxHeight={'575px'}
          onSort={onTableSort}
          isLoading={isLoading}
        />
      </DashboardTableWrapper>
      <Paging
        currentPage={currentPage}
        totalItems={totalInvoices}
        itemsByPage={pageSize}
        onChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[25, 50, 100]}
      />
    </PaddedCard>
  );
};
